import LegalLayout from "@/components/LegalLayout";
import { COMPANY } from "@/data/company";

const H = ({ children }) => (
  <h2 className="font-display text-2xl font-bold text-slate-900 mt-10 mb-3">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-lg font-semibold text-slate-900 mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="text-slate-700">{children}</p>;
const Ul = ({ children }) => <ul className="list-disc pl-6 space-y-2 text-slate-700">{children}</ul>;

const PrivacyPolicy = () => {
  return (
    <LegalLayout title="Politica de confidențialitate" lastUpdated={COMPANY.legalLastUpdated}>
      <P>
        Această politică de confidențialitate descrie modul în care{" "}
        <strong>{COMPANY.legalName}</strong> (CUI: {COMPANY.cui}), denumită în continuare „C2
        Advertising" sau „noi", colectează, utilizează și protejează datele
        personale ale vizitatorilor și clienților site-ului{" "}
        <strong>{COMPANY.website}</strong>, în conformitate cu Regulamentul (UE) 2016/679
        (GDPR) și cu legislația română în vigoare.
      </P>

      <H>1. Operatorul de date</H>
      <Ul>
        <li><strong>Denumire:</strong> {COMPANY.legalName}</li>
        <li><strong>CUI:</strong> {COMPANY.cui}</li>
        <li><strong>Nr. Reg. Comerțului:</strong> {COMPANY.regCom}</li>
        <li><strong>Sediu:</strong> {COMPANY.address}, {COMPANY.city}, {COMPANY.country}</li>
        <li><strong>Email contact:</strong> {COMPANY.email}</li>
        <li><strong>Email GDPR / responsabil protecția datelor:</strong> {COMPANY.dpoEmail}</li>
      </Ul>

      <H>2. Ce date colectăm</H>
      <P>
        Colectăm doar datele strict necesare pentru a răspunde cererilor tale și pentru a oferi
        serviciile noastre. Concret:
      </P>
      <Ul>
        <li>
          <strong>Date de identificare și contact</strong> (când completezi formularul de
          contact): nume, email, telefon, denumirea companiei.
        </li>
        <li>
          <strong>Conținutul comunicării</strong>: mesajul tău, serviciul de interes, bugetul
          estimativ menționat.
        </li>
        <li>
          <strong>Date tehnice automate</strong>: adresă IP (anonimizată), tipul de browser,
          paginile vizitate, durata vizitei — dacă ai acceptat cookies de analiză.
        </li>
      </Ul>

      <H>3. Scopurile prelucrării</H>
      <Ul>
        <li>Pentru a răspunde la cererea ta de ofertă sau de informații.</li>
        <li>Pentru a încheia și executa contracte de prestări servicii.</li>
        <li>Pentru îmbunătățirea site-ului și a serviciilor (statistici anonime).</li>
        <li>Pentru îndeplinirea obligațiilor legale (fiscale, contabile).</li>
      </Ul>

      <H>4. Temeiul legal al prelucrării</H>
      <Ul>
        <li>
          <strong>Consimțământul tău</strong> (art. 6 alin. 1 lit. a GDPR) — pentru formularul
          de contact și pentru cookies de marketing/analiză.
        </li>
        <li>
          <strong>Executarea unui contract</strong> (art. 6 alin. 1 lit. b GDPR) — când devii
          client.
        </li>
        <li>
          <strong>Obligație legală</strong> (art. 6 alin. 1 lit. c GDPR) — pentru evidența
          contabilă și fiscală.
        </li>
        <li>
          <strong>Interes legitim</strong> (art. 6 alin. 1 lit. f GDPR) — pentru îmbunătățirea
          serviciilor și prevenirea fraudelor.
        </li>
      </Ul>

      <H>5. Durata de păstrare</H>
      <Ul>
        <li>
          Datele din formularul de contact: <strong>maxim 24 de luni</strong> dacă nu devii
          client.
        </li>
        <li>
          Datele clienților: pe durata contractului și{" "}
          <strong>10 ani</strong> ulterior pentru documentele financiar-contabile, conform
          legii.
        </li>
        <li>Datele anonime de analiză: până la 26 de luni (Google Analytics).</li>
      </Ul>

      <H>6. Cui transmitem datele</H>
      <P>
        Nu vindem și nu închiriem datele tale. Le putem transmite doar către:
      </P>
      <Ul>
        <li>
          <strong>Furnizori de servicii IT</strong> (hosting, email transactional, analytics) —
          în calitate de persoane împuternicite, în UE/SEE sau pe baza clauzelor contractuale
          standard.
        </li>
        <li>
          <strong>Autorități publice</strong> — doar dacă există obligație legală.
        </li>
        <li>
          <strong>Consultanți contabili și juridici</strong> — sub obligația profesională de
          confidențialitate.
        </li>
      </Ul>

      <H>7. Drepturile tale (GDPR)</H>
      <Ul>
        <li><strong>Dreptul de acces</strong> la datele personale.</li>
        <li><strong>Dreptul la rectificare</strong> a datelor inexacte.</li>
        <li><strong>Dreptul la ștergere</strong> („dreptul de a fi uitat").</li>
        <li><strong>Dreptul la restricționarea</strong> prelucrării.</li>
        <li><strong>Dreptul la portabilitate</strong> a datelor.</li>
        <li><strong>Dreptul de a te opune</strong> prelucrării.</li>
        <li>
          <strong>Dreptul de a-ți retrage consimțământul</strong> oricând, fără a afecta
          legalitatea prelucrării anterioare.
        </li>
        <li>
          <strong>Dreptul de a depune o plângere</strong> la Autoritatea Națională de
          Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP) —{" "}
          <a
            href="https://www.dataprotection.ro"
            target="_blank"
            rel="noopener noreferrer"
            className="text-magenta hover:underline font-semibold"
          >
            www.dataprotection.ro
          </a>
          .
        </li>
      </Ul>
      <P>
        Pentru exercitarea oricărui drept, contactează-ne la{" "}
        <a
          href={`mailto:${COMPANY.dpoEmail}`}
          className="text-magenta hover:underline font-semibold"
        >
          {COMPANY.dpoEmail}
        </a>
        . Răspundem în maxim 30 de zile.
      </P>

      <H>8. Securitatea datelor</H>
      <P>
        Folosim măsuri tehnice și organizatorice rezonabile pentru a proteja datele:
        criptare HTTPS, control acces, backup-uri și politici interne de
        confidențialitate.
      </P>

      <H>9. Modificări ale politicii</H>
      <P>
        Putem actualiza această politică periodic. Versiunea curentă este afișată
        permanent pe această pagină, cu data ultimei actualizări.
      </P>

      <H>10. Contact</H>
      <P>
        Pentru orice întrebări privind prelucrarea datelor tale, scrie-ne la{" "}
        <a
          href={`mailto:${COMPANY.dpoEmail}`}
          className="text-magenta hover:underline font-semibold"
        >
          {COMPANY.dpoEmail}
        </a>{" "}
        sau folosește formularul de contact de pe site.
      </P>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
